import React from 'react';
import { useDisclosure } from '@heroui/react';
import { useDeleteIntakeMutation, useHideIntakeMutation, Intake } from '@/entities/Courses';
import { Svg } from '@/shared/ui/Svg';
import { useToast } from '@/shared/lib/hooks/useToast';
import { MessageModal } from '@/shared/ui/Modal';
import EditIcon from '@/shared/assets/icons/edit.svg';
import OpenedEyeIcon from '@/shared/assets/icons/opened-eye.svg';
import ClosedEyeIcon from '@/shared/assets/icons/slashed-eye.svg';
import TrashIcon from '@/shared/assets/icons/trash.svg';
import ChevronDownIcon from '@/shared/assets/icons/chevron-down.svg';
import styles from './IntakeActions.module.scss';

interface IntakeActionsProps {
  intake: Intake;
  onToggle?: () => void;
  isExpanded?: boolean;
  hasGroups?: boolean;
  onEdit?: (intakeId: number) => void;
  allowEdit?: boolean;
  allowHide?: boolean;
  allowDelete?: boolean;
}

export const IntakeActions: React.FC<IntakeActionsProps> = ({
  intake,
  onToggle,
  isExpanded = false,
  hasGroups = false,
  onEdit,
  allowEdit = true,
  allowHide = true,
  allowDelete = true,
}) => {
  const { toast } = useToast();
  const {
    isOpen: isDeleteModalOpen,
    onOpen: onDeleteModalOpen,
    onOpenChange: onDeleteModalOpenChange,
  } = useDisclosure();
  const {
    isOpen: isHideModalOpen,
    onOpen: onHideModalOpen,
    onOpenChange: onHideModalOpenChange,
  } = useDisclosure();

  const [deleteIntake, { isLoading: isDeleting }] = useDeleteIntakeMutation();
  const [hideIntake, { isLoading: isHiding }] = useHideIntakeMutation();

  const handleEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    onEdit?.(intake.id);
  };

  const handleHide = (e: React.MouseEvent) => {
    e.stopPropagation();
    onHideModalOpen();
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDeleteModalOpen();
  };

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    onToggle?.();
  };

  const handleDeleteIntake = async () => {
    try {
      await deleteIntake(intake.id).unwrap();
      toast({
        title: 'Success',
        description: 'Intake deleted successfully',
        color: 'success',
      });
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error?.data?.message || 'Failed to delete intake',
        color: 'danger',
      });
    }
    onDeleteModalOpenChange();
  };

  const handleHideIntake = async () => {
    try {
      const isVisible = !intake.isVisible;
      await hideIntake({ id: intake.id, body: { isVisible } }).unwrap();
      toast({
        title: 'Success',
        description: isVisible ? 'Intake shown successfully' : 'Intake hidden successfully',
        color: 'success',
      });
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error?.data?.message || 'Failed to update intake visibility',
        color: 'danger',
      });
    }
    onHideModalOpenChange();
  };

  return (
    <>
      <div className={styles.actions}>
        {allowEdit && (
          <button type="button" onClick={handleEdit} className={styles.actionButton}>
            <Svg Icon={EditIcon} width={20} height={20} />
          </button>
        )}
        {allowHide && (
          <button type="button" onClick={handleHide} className={styles.actionButton}>
            <Svg
              Icon={intake.isVisible ? OpenedEyeIcon : ClosedEyeIcon}
              width={20}
              height={20}
              className="text-grey-secondary"
            />
          </button>
        )}
        {allowDelete && (
          <button type="button" onClick={handleDelete} className={styles.actionButton}>
            <Svg Icon={TrashIcon} width={20} height={20} className="text-icon-dizlike" />
          </button>
        )}
        {hasGroups && (
          <button
            type="button"
            onClick={handleToggle}
            className={styles.actionButton}
            aria-label={isExpanded ? 'Collapse groups' : 'Expand groups'}
            aria-expanded={isExpanded}
          >
            <Svg
              Icon={ChevronDownIcon}
              width={20}
              height={20}
              className={isExpanded ? styles.chevronUp : ''}
            />
          </button>
        )}
      </div>
      <MessageModal
        title="Delete Intake"
        text={`Are you sure you want to delete the intake "${intake.title}"? This action cannot be undone.`}
        confirmText="Delete"
        isOpen={isDeleteModalOpen}
        onOpenChange={onDeleteModalOpenChange}
        variant="red"
        toggleVisibility={handleDeleteIntake}
        isLoading={isDeleting}
      />
      <MessageModal
        title={intake.isVisible ? 'Hide Intake' : 'Show Intake'}
        text={`Are you sure you want to ${intake.isVisible ? 'hide' : 'show'} the intake "${intake.title}"?`}
        confirmText={intake.isVisible ? 'Hide' : 'Show'}
        isOpen={isHideModalOpen}
        onOpenChange={onHideModalOpenChange}
        variant="black"
        toggleVisibility={handleHideIntake}
        isLoading={isHiding}
      />
    </>
  );
};
